import {
  useLayoutEffect,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react'

const GRID_SCROLL_MAX_HEIGHT_VAR = '--page-grid-scroll-max-height'

export function usePageContentGridScrollMaxHeight(
  sectionRef: RefObject<HTMLElement | null>,
  deps: readonly unknown[] = [],
  options?: {
    disabled?: boolean
    headerSelector?: string
    pagerSelector?: string
    /** Measure from the element's top instead of bottom (scroll the wrapper, not tbody). */
    measureFrom?: 'top' | 'bottom'
  },
): CSSProperties | undefined {
  const [maxHeight, setMaxHeight] = useState<number | null>(null)

  useLayoutEffect(() => {
    if (options?.disabled) {
      setMaxHeight(null)
      return
    }

    const section = sectionRef.current
    if (!section) return

    const measureGridBodyMaxHeight = (): void => {
      const pageContent = section.closest('.page-content')
      if (!pageContent) return

      const header = section.querySelector(
        options?.headerSelector ?? '.e-gridheader',
      )
      const pager = section.querySelector(
        options?.pagerSelector ?? '.pagination-container',
      )
      const headerRect = header?.getBoundingClientRect()
      const headerEdge =
        options?.measureFrom === 'top'
          ? (headerRect?.top ?? section.getBoundingClientRect().top)
          : (headerRect?.bottom ?? section.getBoundingClientRect().top)
      const pageBottom = pageContent.getBoundingClientRect().bottom
      const pagerHeight = pager?.getBoundingClientRect().height ?? 0
      const available = pageBottom - headerEdge - pagerHeight - 16

      setMaxHeight(Math.max(120, Math.floor(available)))
    }

    measureGridBodyMaxHeight()

    const pageContent = section.closest('.page-content')
    const resizeObserver = new ResizeObserver(measureGridBodyMaxHeight)
    resizeObserver.observe(section)
    if (pageContent) resizeObserver.observe(pageContent)

    window.addEventListener('resize', measureGridBodyMaxHeight)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', measureGridBodyMaxHeight)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  if (maxHeight == null) return undefined

  return {
    [GRID_SCROLL_MAX_HEIGHT_VAR]: `${maxHeight}px`,
  } as CSSProperties
}
